import React from 'react';
import styled from 'styled-components';

import { Chip, Icon, formatNumber } from '@column/column-ui-kit';

import { TransferHeaderFrame } from '../../_components/TransferHeaderFrame';
import { CheckScannerIcon } from '~/elements/icons/transfers/CheckScanner';
import { Flex, Text } from '~/styles';
import { formatDate, formatDateTime } from '~/util';

const TransferMeta = styled.ul`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 6px 16px;
  list-style: none;
  flex-wrap: wrap;

  li {
    display: flex;
    align-items: center;
  }
`;

export const InfoForm = styled.form`
  border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(200)};
`;

const DateTimeDisplay = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const DateTimeLocal = styled.span`
  transition: opacity 0.2s ease-in-out;
  opacity: 1;
  position: absolute;
  left: 0;
`;

const DateTimeUTC = styled(DateTimeLocal)`
  opacity: 0;
  position: relative;
`;

const DayLocal = styled.span`
  position: absolute;
  top: 2px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  transition: opacity 0.2s ease-in-out;
`;

const DayUTC = styled(DayLocal)`
  opacity: 0;
`;

const DayDisplay = styled.div`
  position: relative;

  .icon-calendar-line {
    display: none;
  }
`;

const Date = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};

  &:hover {
    ${DateTimeLocal},
    ${DayLocal} {
      opacity: 0;
    }

    ${DateTimeUTC},
    ${DayUTC} {
      opacity: 1;
    }
  }
`;

interface TransferBatchHeaderProps {
  amount: number | string;
  createdAt: string;
  currencyCode?: string;
  transferBatchId: string;
  status: 'pending' | 'deposited';
  totalCount: number;
}

export const TransferBatchHeader: React.FC<React.PropsWithChildren<TransferBatchHeaderProps>> = ({
  amount,
  createdAt,
  currencyCode,
  transferBatchId,
  status,
  totalCount,
}) => {
  const numericAmount = Number(amount);
  const amountColor = numericAmount > 0 ? 'var(--color-seafoam-600)' : 'var(--color-foreground)';

  return (
    <TransferHeaderFrame idLabel="Batch ID" idValue={transferBatchId}>
      <Flex flexDirection="column" gap={8}>
        <Flex gap={8}>
          <Flex alignItems="baseline" gap={4}>
            <Text color={amountColor} letterSpacing="tighter" size={24} weight="semibold">
              {formatNumber(amount, currencyCode)}
            </Text>
            <Text size={14} color="lighter" weight="medium">
              {currencyCode || 'USD'}
            </Text>
          </Flex>
        </Flex>

        <TransferMeta>
          <li>
            <Chip icon={<CheckScannerIcon />} size="large" variant="subtle">
              {totalCount} Checks
            </Chip>
          </li>
          <li>
            <Chip size="large" variant="subtle">
              {status === 'deposited' ? 'Deposited' : 'Pending'}
            </Chip>
          </li>
          <li>
            <Date>
              <DayDisplay>
                <DayLocal>
                  {createdAt
                    ? formatDate(createdAt, { dateFormat: 'short', timeZoneFormat: 'local' })
                        .split(' ')[1]
                        .replace(',', '')
                    : ''}
                </DayLocal>
                <DayUTC>
                  {createdAt
                    ? formatDate(createdAt, { dateFormat: 'short', timeZoneFormat: 'utc' })
                        .split(' ')[1]
                        .replace(',', '')
                    : ''}
                </DayUTC>
                <Icon.Calendar />
              </DayDisplay>
              <DateTimeDisplay>
                <DateTimeLocal>
                  <Text size={14} color="currentColor" weight="medium" whiteSpace="nowrap">
                    {formatDateTime(createdAt, { timeZoneFormat: 'local' })}
                  </Text>
                </DateTimeLocal>
                <DateTimeUTC>
                  <Text size={14} color="currentColor" weight="medium" whiteSpace="nowrap">
                    {formatDateTime(createdAt, { timeZoneFormat: 'utc' })}
                  </Text>
                </DateTimeUTC>
              </DateTimeDisplay>
            </Date>
          </li>
        </TransferMeta>
      </Flex>
    </TransferHeaderFrame>
  );
};
